// HelpSupportPage.tsx · v12.10.30.1 (#2264 the left menu is now 'Help desk' too) · 2026-11-03 · VoiceSpectrum AI
// v12.10.30 (#2259): Tushar 11/3 - "for help desk i think this screen itself we can make self service portal. with the similar
// switches ... by clicking on help desk, everything is loaded on this screen"; "so we are taking everything in do not leave
// anything behind". The left-menu Help page (/guides) becomes Help & Support:
//   - the band ABOVE the title (Tushar's screenshot): what is new for this person - ticket answers / closes since they last
//     looked, and the newest release; each one opens its switch; hidden when nothing is new
//   - switches Help (today's Help page - GuidesHome, unchanged, practice features #2256 included) · My tickets · What's new
//   - My tickets: the person's tickets (Security › Users › View: the whole practice's) - number, subject, status, last update,
//     the conversation with Support, Reply, and New ticket = the sign-in page's Contact support fields, filled in
//   - What's new: the release notes per build (/whats-new.json from the packager)
// The account menu's Help desk opens ?tab=tickets; the left-menu Help desk (v12.10.30.1 #2264, was 'Help') opens the Help switch.
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import GuidesHome from './GuidesHome';
import {
  HELP_SUPPORT_CAPTION, TICKET_STATUS_LABEL, helpDeskApi, loadWhatsNew, markNewsSeen, markTicketSeen, newsUnread, readSeen,
  ticketUnread, compareVersions, type HelpSupportTab, type NewTicket, type Ticket, type TicketList, type WhatsNew,
} from '../../lib/helpDesk';
import { ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import './help.css';
import './helpdesk.css';
import '../../components/firstrun/firstrun.css';   // the fr-btn / fr-muted buttons and notes the Help page already uses

const TABS: { key: HelpSupportTab; label: string }[] = [
  { key: 'help', label: 'Help' },
  { key: 'tickets', label: 'My tickets' },
  { key: 'news', label: 'What’s new' },
];
const isTab = (v: string | null): v is HelpSupportTab => v === 'help' || v === 'tickets' || v === 'news';
const TICKETS_KEY = ['t2-help-desk-tickets'];

function when(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = d.toDateString() === new Date().toDateString();
  return today ? `today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
/** "2026-11-03" -> "Nov 3, 2026" (a calendar day - no time zone shift). */
function dayOnly(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00`);
  return isNaN(d.getTime()) ? ymd : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}
const errText = (e: unknown, fallback: string) => {
  const b = (e instanceof ApiError ? e.body : null) as { message?: string; errors?: Record<string, string> } | null;
  return b?.message ?? (b?.errors ? Object.values(b.errors)[0] : null) ?? fallback;
};

export default function HelpSupportPage() {
  const [params, setParams] = useSearchParams();
  const tab: HelpSupportTab = isTab(params.get('tab')) ? (params.get('tab') as HelpSupportTab) : 'help';
  const openId = Number(params.get('t')) || null;
  const [seenTick, setSeenTick] = useState(0);   // re-read "seen" after marking

  const tickets = useQuery<TicketList>({ queryKey: TICKETS_KEY, queryFn: helpDeskApi.list, staleTime: 30_000, refetchInterval: 120_000, refetchOnWindowFocus: true, retry: false });
  const news = useQuery<WhatsNew | null>({ queryKey: ['t2-whats-new'], queryFn: loadWhatsNew, staleTime: 10 * 60_000, retry: false });
  const who = tickets.data?.me.email || 'me';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seen = useMemo(() => readSeen(who), [who, seenTick, tickets.data, news.data]);

  function go(next: HelpSupportTab, ticketId?: number) {
    const p: Record<string, string> = {};
    if (next !== 'help') p.tab = next;
    if (ticketId) p.t = String(ticketId);
    setParams(p);
  }

  // What's new: opening the switch marks the newest release seen.
  useEffect(() => {
    const top = news.data?.versions[0]?.version;
    if (tab === 'news' && top && tickets.data) { markNewsSeen(who, top); setSeenTick((n) => n + 1); }
  }, [tab, news.data, who, tickets.data]);

  const unreadTickets = (tickets.data?.items ?? []).filter((t) => ticketUnread(t, seen));
  const newsNew = newsUnread(news.data ?? null, seen) && tab !== 'news';
  const latest = news.data?.versions[0];

  return (
    <div className="hs-page" data-testid="help-support-page">
      {/* v12.10.30 (#2259): the band above the title - only when something is new */}
      {(unreadTickets.length > 0 || newsNew) && (
        <div className="hs-strip" role="status" aria-label="New for you">
          {unreadTickets.slice(0, 3).map((t) => (
            <button key={t.id} type="button" className="hs-note" onClick={() => go('tickets', t.id)}>
              <span className="hs-note-ic" aria-hidden="true">💬</span>
              Your ticket <b>{t.reference}</b> was {t.status === 'closed' ? 'closed' : 'answered'}<span className="hs-note-sub"> · {t.subject}</span>
              <span className="hs-note-go">Open ›</span>
            </button>
          ))}
          {unreadTickets.length > 3 && (
            <button type="button" className="hs-note" onClick={() => go('tickets')}>
              <span className="hs-note-ic" aria-hidden="true">💬</span>{unreadTickets.length - 3} more ticket updates<span className="hs-note-go">My tickets ›</span>
            </button>
          )}
          {newsNew && latest && (
            <button type="button" className="hs-note hs-note--news" onClick={() => go('news')}>
              <span className="hs-note-ic" aria-hidden="true">✨</span>
              What’s new in <b>version {latest.version}</b><span className="hs-note-sub"> · {latest.items[0]?.text}{latest.items.length > 1 ? ` and ${latest.items.length - 1} more` : ''}</span>
              <span className="hs-note-go">See what’s new ›</span>
            </button>
          )}
        </div>
      )}

      <div className="hs-top">
        <h1>{HELP_SUPPORT_CAPTION}</h1>
        <div className="hs-tabs" role="tablist" aria-label={HELP_SUPPORT_CAPTION}>
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`hs-tab${tab === t.key ? ' on' : ''}`}
              data-track={`help-support.tab.${t.key}`} onClick={() => go(t.key)}>
              {t.label}
              {t.key === 'tickets' && unreadTickets.length > 0 && <span className="hs-badge" aria-label={`${unreadTickets.length} new`}>{unreadTickets.length}</span>}
              {t.key === 'news' && newsNew && <span className="hs-dot" aria-label="new" />}
            </button>
          ))}
        </div>
      </div>

      {tab === 'help' && <GuidesHome embedded />}
      {tab === 'tickets' && <MyTickets list={tickets.data} loading={tickets.isLoading} failed={tickets.isError} openId={openId}
        onOpen={(id) => go('tickets', id ?? undefined)} onSeen={(t) => { markTicketSeen(who, t); setSeenTick((n) => n + 1); }} seen={seen} />}
      {tab === 'news' && <WhatsNewPanel news={news.data ?? null} loading={news.isLoading} seenBefore={seen.news} />}
    </div>
  );
}

// ═════ My tickets ═══════════════════════════════════════════════════════════════════════════════════════════════════
type Filter = 'open' | 'closed' | 'all';
function MyTickets({ list, loading, failed, openId, onOpen, onSeen, seen }: {
  list?: TicketList; loading: boolean; failed: boolean; openId: number | null;
  onOpen: (id: number | null) => void; onSeen: (t: Ticket) => void; seen: ReturnType<typeof readSeen>;
}) {
  const [filter, setFilter] = useState<Filter>('open');
  const [scope, setScope] = useState<'mine' | 'practice'>('mine');
  const [composing, setComposing] = useState(false);
  const me = list?.me;
  const all = (list?.items ?? []).filter((t) => (me?.all && scope === 'practice') || t.mine || me?.support);
  const shown = all.filter((t) => filter === 'all' || (filter === 'closed' ? t.status === 'closed' : t.status !== 'closed'))
    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
  const count = (f: Filter) => all.filter((t) => f === 'all' || (f === 'closed' ? t.status === 'closed' : t.status !== 'closed')).length;
  const detailOpen = composing || openId !== null;

  return (
    <div className={`hs-tk${detailOpen ? ' hs-tk--detail' : ''}`}>
      <div className="hs-tk-list">
        <div className="hs-tk-bar">
          <div className="hs-pills" role="radiogroup" aria-label="Which tickets">
            {(['open', 'closed', 'all'] as Filter[]).map((f) => (
              <button key={f} type="button" role="radio" aria-checked={filter === f} className={filter === f ? 'on' : undefined} onClick={() => setFilter(f)}>
                {f === 'open' ? 'Open' : f === 'closed' ? 'Closed' : 'All'} · {count(f)}
              </button>
            ))}
          </div>
          {me?.all && !me.support && (
            <div className="hs-pills" role="radiogroup" aria-label="Whose tickets">
              <button type="button" role="radio" aria-checked={scope === 'mine'} className={scope === 'mine' ? 'on' : undefined} onClick={() => setScope('mine')}>Mine</button>
              <button type="button" role="radio" aria-checked={scope === 'practice'} className={scope === 'practice' ? 'on' : undefined} onClick={() => setScope('practice')}>Whole practice</button>
            </div>
          )}
          <span className="hs-sp" />
          {!me?.support && <button type="button" className="fr-btn p" onClick={() => { setComposing(true); onOpen(null); }}>+ New ticket</button>}
        </div>
        {loading ? <p className="fr-muted">Loading your tickets…</p>
          : failed ? <p className="fr-muted">Your tickets could not be loaded just now. Try again in a minute, or email support@voicespectrum.ai.</p>
          : shown.length === 0 ? (
            <div className="hs-empty">
              <b>{filter === 'closed' ? 'No closed tickets.' : 'No open tickets.'}</b>
              <p>Something not working, or a question for us? Send a ticket - VoiceSpectrum Support answers here and by email.</p>
            </div>
          ) : (
            <ul className="hs-rows">
              {shown.map((t) => {
                const unread = ticketUnread(t, seen);
                return (
                  <li key={t.id}>
                    <button type="button" className={`hs-row${openId === t.id ? ' on' : ''}${unread ? ' unread' : ''}`} onClick={() => { setComposing(false); onOpen(t.id); }}>
                      <span className="hs-row-top">
                        {unread && <span className="hs-dot" aria-label="new" />}
                        <b>{t.reference}</b>
                        <span className={`hs-st hs-st--${t.status}`}>{TICKET_STATUS_LABEL[t.status]}</span>
                        <span className="hs-sp" />
                        <span className="hs-when">{when(t.lastUpdate)}</span>
                      </span>
                      <span className="hs-row-sub">{t.subject || '(no text)'}</span>
                      {!t.mine && <span className="hs-row-who">From {t.name || t.contact}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
      </div>
      <div className="hs-tk-detail">
        {composing && me ? <NewTicketForm me={me} onCancel={() => setComposing(false)} onSent={(t) => { setComposing(false); onOpen(t.id); }} />
          : openId !== null ? <TicketView id={openId} onBack={() => onOpen(null)} onSeen={onSeen} canWrite={!me?.support} />
          : <div className="hs-pick"><p>Pick a ticket to read the conversation, or send a new one.</p></div>}
      </div>
    </div>
  );
}

function TicketView({ id, onBack, onSeen, canWrite }: { id: number; onBack: () => void; onSeen: (t: Ticket) => void; canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery<Ticket>({ queryKey: ['t2-help-desk-ticket', id], queryFn: () => helpDeskApi.get(id), retry: false, refetchInterval: 60_000 });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const t = q.data;
  useEffect(() => { if (t) onSeen(t); /* opening it = seen */ }, [t?.id, t?.supportUpdateAt]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [t?.thread?.length]);

  async function send() {
    if (!text.trim()) { setErr('Type your reply first.'); return; }
    setBusy(true); setErr(null);
    try {
      const next = await helpDeskApi.reply(id, text.trim());
      qc.setQueryData(['t2-help-desk-ticket', id], next);
      await qc.invalidateQueries({ queryKey: TICKETS_KEY });
      setText('');
      toast('success', `Added to ${next.reference}`, 'VoiceSpectrum Support has it.');
    } catch (e) { setErr(errText(e, 'That did not send. Try again, or email support@voicespectrum.ai.')); }
    finally { setBusy(false); }
  }

  if (q.isLoading) return <p className="fr-muted">Loading…</p>;
  if (!t) return (
    <div className="hs-pick"><p>That ticket is not one you can open.</p><button type="button" className="fr-btn" onClick={onBack}>‹ My tickets</button></div>
  );
  return (
    <article className="hs-ticket" aria-label={`Ticket ${t.reference}`}>
      <header className="hs-ticket-h">
        <button type="button" className="fr-btn hs-back" onClick={onBack}>‹ My tickets</button>
        <h2>{t.reference}</h2>
        <span className={`hs-st hs-st--${t.status}`}>{TICKET_STATUS_LABEL[t.status]}</span>
        <span className="hs-sp" />
        <span className="hs-when">Sent {when(t.createdAt)}</span>
      </header>
      <div className="hs-thread">
        <div className="hs-turn hs-turn--them">
          <div className="hs-who">{t.mine ? 'You' : (t.name || t.contact)} · {when(t.createdAt)}</div>
          <div className="hs-body">{t.message}</div>
        </div>
        {(t.thread ?? []).map((m) => (
          <div key={m.id} className={`hs-turn ${m.from === 'support' ? 'hs-turn--us' : 'hs-turn--them'}`}>
            <div className="hs-who">{m.from === 'support' ? 'VoiceSpectrum Support' : m.mine ? 'You' : m.who} · {when(m.at)}</div>
            <div className="hs-body">{m.body}</div>
          </div>
        ))}
        {(t.thread ?? []).every((m) => m.from !== 'support') && t.status !== 'closed' && (
          <p className="fr-muted hs-wait">Support has your ticket and will answer here{t.contact.includes('@') ? ` and by email to ${t.contact}` : ''}.</p>
        )}
        <div ref={endRef} />
      </div>
      {canWrite && (
        <div className="hs-reply">
          <label htmlFor="hs-reply-t">{t.status === 'closed' ? 'Write again - this opens the ticket again' : 'Reply'}</label>
          <textarea id="hs-reply-t" rows={3} maxLength={2000} value={text} onChange={(e) => { setText(e.target.value); if (err) setErr(null); }}
            aria-invalid={!!err || undefined} placeholder="Add details, or answer Support’s question…" />
          {err && <div className="hs-err" role="alert">{err}</div>}
          <div className="hs-reply-b"><button type="button" className="fr-btn p" disabled={busy || !text.trim()} onClick={() => void send()}>{busy ? 'Sending…' : 'Send'}</button></div>
        </div>
      )}
    </article>
  );
}

/** The sign-in page's Contact support form (login/views.tsx SupportView - same four fields, same rules), signed in: filled in. */
function NewTicketForm({ me, onCancel, onSent }: { me: TicketList['me']; onCancel: () => void; onSent: (t: Ticket) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<NewTicket>({ name: me.name, practice: me.practice, contact: me.email, message: '' });
  const [err, setErr] = useState<{ k: keyof NewTicket | 'form'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const msgRef = useRef<HTMLTextAreaElement>(null);
  const contactRef = useRef<HTMLInputElement>(null);
  useEffect(() => { msgRef.current?.focus(); }, []);
  useEffect(() => { if (err?.k === 'contact') contactRef.current?.focus(); else if (err?.k === 'message') msgRef.current?.focus(); }, [err]);
  const set = (k: keyof NewTicket, v: string) => { setF((x) => ({ ...x, [k]: v })); if (err?.k === k) setErr(null); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.contact.trim()) { setErr({ k: 'contact', text: 'Enter your email or mobile so support can reply.' }); return; }
    if (!f.message.trim()) { setErr({ k: 'message', text: 'Tell us what’s happening.' }); return; }
    setBusy(true); setErr(null);
    try {
      const r = await helpDeskApi.create({ name: f.name.trim(), practice: f.practice.trim(), contact: f.contact.trim(), message: f.message.trim() });
      await qc.invalidateQueries({ queryKey: TICKETS_KEY });
      toast('success', `We got it - ${r.reference}`, 'VoiceSpectrum Support answers here and by email.');
      onSent(r.ticket);
    } catch (e2) { setErr({ k: 'form', text: errText(e2, 'We couldn’t send that just now. Try again, or email support@voicespectrum.ai.') }); }
    finally { setBusy(false); }
  }

  return (
    <form className="hs-new" onSubmit={submit} aria-label="New ticket" noValidate>
      <header className="hs-ticket-h">
        <button type="button" className="fr-btn hs-back" onClick={onCancel}>‹ My tickets</button>
        <h2>New ticket</h2>
      </header>
      <p className="fr-muted">Tell us what’s wrong - we’ll get back to you.</p>
      <div className="hs-fields">
        <label>Your name<input value={f.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" maxLength={200} /></label>
        <label>Practice<input value={f.practice} onChange={(e) => set('practice', e.target.value)} autoComplete="organization" maxLength={200} /></label>
        <label>Email or mobile *
          <input ref={contactRef} value={f.contact} onChange={(e) => set('contact', e.target.value)} autoComplete="email" autoCapitalize="none" spellCheck={false} maxLength={320}
            aria-invalid={err?.k === 'contact' || undefined} className={err?.k === 'contact' ? 'is-err' : undefined} />
        </label>
        {err?.k === 'contact' && <div className="hs-err" role="alert">{err.text}</div>}
        <label>What’s happening *
          <textarea ref={msgRef} rows={5} value={f.message} onChange={(e) => set('message', e.target.value)} maxLength={2000}
            placeholder="The first line becomes the ticket’s subject…" aria-invalid={err?.k === 'message' || undefined} className={err?.k === 'message' ? 'is-err' : undefined} />
        </label>
        {err?.k === 'message' && <div className="hs-err" role="alert">{err.text}</div>}
      </div>
      {err?.k === 'form' && <div className="hs-err" role="alert">{err.text}</div>}
      <div className="hs-reply-b">
        <button type="button" className="fr-btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="fr-btn p" disabled={busy}>{busy ? 'Sending…' : 'Send to support'}</button>
      </div>
    </form>
  );
}

// ═════ What's new ═══════════════════════════════════════════════════════════════════════════════════════════════════
function WhatsNewPanel({ news, loading, seenBefore }: { news: WhatsNew | null; loading: boolean; seenBefore?: string }) {
  // what was new when the switch opened (opening it marks the newest seen - keep the "New" tags for this visit)
  const [before] = useState(seenBefore);
  if (loading) return <p className="fr-muted hs-pad">Loading…</p>;
  if (!news || news.versions.length === 0) return (
    <div className="hs-empty hs-pad"><b>Nothing here yet.</b><p>What changed in each VoiceSpectrum update shows here after the next update.</p></div>
  );
  return (
    <div className="hs-news">
      {news.versions.map((v) => (
        <section key={v.version} className="hs-ver">
          <h2>Version {v.version}{v.date ? <span className="hs-when"> · {dayOnly(v.date)}</span> : null}
            {(!before || compareVersions(v.version, before) > 0) && <span className="hs-new-tag">New</span>}</h2>
          <ul>{v.items.map((it, i) => <li key={`${it.num ?? i}`}>{it.text}{it.area ? <span className="hs-area"> · {it.area}</span> : null}</li>)}</ul>
        </section>
      ))}
    </div>
  );
}
